import React, { useCallback, useMemo } from 'react';
import { flatten, isEmpty, isNull, isUndefined, uniq } from 'lodash/fp';

import styles from './AccessGroups.module.scss';
import { getAccessGroupColumnSort, AccessGroup, getAccessGroupFilters, clearAccessGroupFilters, AccessGroupTableName, getIsFetchingAccessGroupEntities, getFilteredAccessGroupEntities } from './store';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { useAppSelector, useAppDispatch } from '../../../hooks/react-redux';
import { EntityType } from '../entity/store';
import { Toggle } from '../../shared/toggle';
import { capitaliseStartLetter } from '../../../utils/regex-utils';
import { Icon } from '../../shared/icon/Icon';
import { Agency, Eye, Home } from '../../shared/icons';
import { FilterDropdownOptions } from '../../shared/table/types';
import { AccessGroupTableHeader } from './AccessGroupTableHeader';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';

const MODAL_HEADER_AND_PADDING = 126;

interface AccessGroupEntitiesSectionProps {
    accessGroup: AccessGroup;
    modalHeight: number;
    updateGroup: (key: keyof AccessGroup, userIds: number[]) => void;
}

export const AccessGroupEntitiesSection: React.FC<AccessGroupEntitiesSectionProps> = ({ accessGroup, modalHeight, updateGroup }) => {
    const { entityIds } = accessGroup;
    const dispatch = useAppDispatch();

    const accessGroupEntities = useAppSelector(getFilteredAccessGroupEntities);
    const columnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.ENTITY));
    const filters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.ENTITY));
    const isFetchingEntities = useAppSelector(getIsFetchingAccessGroupEntities);

    const toggleEntityInAccessGroup = useCallback((userId: number, value: boolean, type: EntityType, groupEntities: number[]) => {
        if (type === EntityType.COMPANY) {
            const updatedEntityIds = !value ? entityIds.filter(id => id !== userId) : [...entityIds, userId];
            updateGroup('entityIds', updatedEntityIds);
        } else {
            const updatedEntityIds = !value ? entityIds.filter(id => !groupEntities.includes(id)) : uniq([...entityIds, ...groupEntities]);
            updateGroup('entityIds', updatedEntityIds);
        }
    }, [entityIds, updateGroup]);

    const entityInGroup = useCallback((userId: number) => entityIds.includes(userId), [entityIds]);
    const groupEntityInGroup = useCallback((groupEntities: number[]) => groupEntities.every(entityId => entityIds.includes(entityId)), [entityIds]);

    const allSelected = useMemo(() => {
        if (!isEmpty(filters) && !isUndefined(filters) && !isNull(filters)) {
            const typeColumn = filters['type'];
            if (!isUndefined(typeColumn)) {
                const dropdownFilter = !isNull(filters['type'].dropdown);
                if (dropdownFilter && filters['type'].dropdown!.includes('group')) {
                    const allEntityGroupEntities = uniq(flatten(accessGroupEntities.map(({ groupEntities }) => groupEntities)));
                    return allEntityGroupEntities.length > 0 && allEntityGroupEntities.every(entityId => entityIds.includes(entityId));
                }
            }
        }
        return accessGroupEntities.length > 0 && accessGroupEntities.filter(({ type }) => type === EntityType.COMPANY).every(({ entityId }) => entityIds.includes(entityId));
    }, [entityIds, accessGroupEntities, filters]);

    const toggleSelectAll = useCallback(() => {
        if (!isEmpty(filters) && !!filters && !isUndefined(filters['type']) && !isNull(filters['type'].dropdown) && filters['type'].dropdown!.includes('group')) {
            const allEntityGroupEntities = uniq(flatten(accessGroupEntities.map(({ groupEntities }) => groupEntities)));
            updateGroup('entityIds', allEntityGroupEntities);
        } else {
            const entityIdsForSelected = flatten(accessGroupEntities.map(({ entityId, type, groupEntities }) => type === EntityType.GROUP ? groupEntities : entityId));
            const updatedEntityIds = allSelected ? [] : entityIdsForSelected;
            updateGroup('entityIds', updatedEntityIds);
        }
    }, [allSelected, accessGroupEntities, updateGroup, filters]);

    const scrollableHeight = useMemo(() => modalHeight - MODAL_HEADER_AND_PADDING, [modalHeight]);

    const filterDropdownOptions: FilterDropdownOptions = { type: [{ label: 'Company', value: EntityType.COMPANY }, { label: 'Group', value: EntityType.GROUP }], status: [{ label: 'My Company', value: 'myCompany' }, { label: 'Can Be Agent', value: 'canBeAgent' }, { label: 'On Watchlist', value: 'onWatchlist' }] };

    const clearFilters = useCallback(() => dispatch(clearAccessGroupFilters(AccessGroupTableName.ENTITY)), [dispatch]);
    const filtersExist = useMemo(() => !!Object.values(filters).filter(({ text, dropdown }) => !isNull(dropdown) || !!text).length || !isUndefined(columnSort), [filters, columnSort]);

    const tableHeaders = [
        { width: '45%', title: 'Name', column: 'entityName' },
        { width: '15%', title: 'Type', column: 'type' },
        { width: '20%', title: 'Status', column: 'status', isIconHeader: true },
        { width: '20%', title: 'Included', column: 'entityIncluded' }
    ];

    return (
        <>
            <div className={styles.tableTitleWrapper}>
                <div className={styles.tableTitle}>Entities</div>
                <div>({entityIds.length} selected - Note this does not include groups but includes their children)</div>
                {filtersExist &&
                    <div className={styles.clearAllFiltersWrapper}>
                        <button className={styles.clearAllFilters} onClick={clearFilters} data-testid='access-group-entity-filter-button'>Clear All Filters</button>
                    </div>
                }
            </div>
            <div className={styles.accessGroupContentWrapper}>
                <AccessGroupTableHeader
                    allSelected={allSelected}
                    toggleSelectAll={toggleSelectAll}
                    textFilterColumns={['entityName']}
                    tableHeaders={tableHeaders}
                    selectAllLabel='Entities'
                    filterDropdownOptions={filterDropdownOptions}
                    filters={filters}
                    columnSort={columnSort}
                    tableName={AccessGroupTableName.ENTITY}
                />
                <Scrollable height={`${scrollableHeight}px`}>
                    {isFetchingEntities ? <div className={styles.tableLoadingDots} style={{ height: scrollableHeight }}><LoadingDots /></div> :
                        accessGroupEntities.map(({ entityId, name, type, canBeAgent, onWatchlist, myCompany, groupEntities }) => {
                            const inGroup = type === EntityType.GROUP ? groupEntityInGroup(groupEntities) : entityInGroup(entityId);
                            return (
                                <div key={entityId} className={styles.tableRow}>
                                    <div className={styles.tableCell} style={{ width: '45%' }}>{name}</div>
                                    <div className={styles.tableCell} style={{ width: '15%' }}>{capitaliseStartLetter(type)}</div>
                                    <div className={styles.tableCell} style={{ width: '20%' }}>
                                        {!!myCompany && <div className={styles.iconWrapper}><Icon icon={Home} /></div>}
                                        {!!canBeAgent && <div className={styles.iconWrapper}><Icon icon={Agency} /></div>}
                                        {!!onWatchlist && <div className={styles.iconWrapper}><Icon icon={Eye} /></div>}
                                    </div>
                                    <div className={styles.tableCell} style={{ width: '20%' }}>
                                        <Toggle
                                            onChange={value => toggleEntityInAccessGroup(entityId, value, type, groupEntities)}
                                            checked={inGroup}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                </Scrollable>
            </div>
        </>
    );
};
